import React from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Progress,
  Typography,
} from '@knockrentals/knock-shared-web-v2';
import { getMegabytes } from '../../../../Utils';
import { makeCss } from '../../../../knock-design-system';
import { UPLOAD_STATUS } from './constants';

export const CANCEL_UPLOAD_LABEL = 'cancel upload';

const useCss = makeCss({
  buttonWrapper: {
    position: 'absolute',
    top: '0',
    right: '0',
  },
  details: {
    flex: 1,
  },
  fileName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  uploadProgress: {
    padding: '32px 8px 8px',
    position: 'relative',
  },
});

const UploadProgress = ({
  abortUpload,
  className,
  fileName,
  fileSize,
  progressValue,
  resetUpload,
  uploadStatus,
}) => {
  const css = useCss();

  const isFailureStatus = uploadStatus === UPLOAD_STATUS.FAILURE;
  const isCancelButtonVisible =
    uploadStatus === UPLOAD_STATUS.UPLOADING || isFailureStatus;

  const handleCloseOnClick = () => {
    if (uploadStatus !== UPLOAD_STATUS.UPLOADING) {
      resetUpload();
      return;
    }
    abortUpload();
  };

  return (
    <div css={css.uploadProgress} className={className}>
      {isCancelButtonVisible && (
        <div css={css.buttonWrapper}>
          <IconButton
            aria-label={CANCEL_UPLOAD_LABEL}
            iconName="close-circle"
            onClick={handleCloseOnClick}
          />
        </div>
      )}

      <div css={css.details}>
        <Typography
          color={isFailureStatus ? 'error.600' : 'text.primary'}
          css={css.fileName}
          variant="subtitle1"
        >
          {fileName}
        </Typography>

        <Typography
          gutterBottom
          color={isFailureStatus ? 'error.600' : 'text.secondary'}
          paragraph
          variant="body2"
        >
          {getMegabytes(fileSize).toFixed(2)} mb • {uploadStatus}
        </Typography>
      </div>

      <Progress
        color={isFailureStatus ? 'secondary' : 'primary'}
        type="linear"
        value={progressValue}
        variant="determinate"
      />
    </div>
  );
};

UploadProgress.propTypes = {
  abortUpload: PropTypes.func,
  className: PropTypes.string,
  fileName: PropTypes.string,
  fileSize: PropTypes.number,
  progressValue: PropTypes.number,
  resetUpload: PropTypes.func,
  uploadStatus: PropTypes.string,
};

export default UploadProgress;
